import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api-client"
import { useParserJobsStore } from "@/stores/parser-jobs-store"

interface ParserJobProgress {
  totalFiles: number
  processedFiles: number
  totalColumns: number
  currentFile: string | null
  currentStep: string
  percentage: number
  errors: string[]
  warnings: string[]
}

export interface ActiveParserJob {
  jobId: string
  sessionId: string
  userId: string
  status: "pending" | "running" | "completed" | "failed" | "cancelled"
  progress: ParserJobProgress
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
}

interface ActiveJobsResponse {
  jobs: Array<{
    job_id: string
    session_id: string
    user_id: string
    status: string
    progress: {
      total_files: number
      processed_files: number
      total_columns: number
      current_file: string | null
      current_step: string
      percentage: number
      errors: string[]
      warnings: string[]
    }
    created_at: string
    started_at: string | null
    completed_at: string | null
    error_message: string | null
  }>
}

function transformJob(job: ActiveJobsResponse["jobs"][0]): ActiveParserJob {
  return {
    jobId: job.job_id,
    sessionId: job.session_id,
    userId: job.user_id,
    status: job.status as ActiveParserJob["status"],
    progress: {
      totalFiles: job.progress.total_files,
      processedFiles: job.progress.processed_files,
      totalColumns: job.progress.total_columns,
      currentFile: job.progress.current_file,
      currentStep: job.progress.current_step,
      percentage: job.progress.percentage,
      errors: job.progress.errors,
      warnings: job.progress.warnings,
    },
    createdAt: job.created_at,
    startedAt: job.started_at,
    completedAt: job.completed_at,
    errorMessage: job.error_message,
  }
}

async function fetchActiveJobs(): Promise<ActiveParserJob[]> {
  const response: ActiveJobsResponse = await api.get("/parser/active-jobs")
  return response.jobs.map(transformJob)
}

const ACTIVE_JOBS_POLL_INTERVAL = 3000
const EMPTY_ACTIVE_JOBS: ActiveParserJob[] = []

export function useActiveParserJobs() {
  const setActiveSessionIds = useParserJobsStore((state) => state.setActiveSessionIds)
  const activeSessionIds = useParserJobsStore((state) => state.activeSessionIds)

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["parserActiveJobs"],
    queryFn: fetchActiveJobs,
    refetchInterval: ACTIVE_JOBS_POLL_INTERVAL,
    staleTime: 0,
  })

  const activeJobs = data ?? EMPTY_ACTIVE_JOBS

  useEffect(() => {
    if (!isSuccess) return
    const sessionIds = activeJobs.map((job) => job.sessionId)
    setActiveSessionIds(sessionIds)
  }, [activeJobs, isSuccess, setActiveSessionIds])

  return {
    activeJobs,
    activeSessionIds,
    isLoading,
    hasActiveJobs: activeJobs.length > 0,
  }
}

export function useIsSessionProcessing(sessionId: string | null | undefined): boolean {
  const isSessionActive = useParserJobsStore((state) => state.isSessionActive)
  if (!sessionId) return false
  return isSessionActive(sessionId)
}
